import { useMemo } from "react";
import type { QueryClient, QueryKey, QueryMeta, QueryState } from "@tanstack/react-query";

/** React Query object that might be annotated with Vyne metadata */
export interface PossiblyVyneQuery<T> {
    meta: QueryMeta | undefined;
    state: QueryState<T>;
}

/** Vyne metadata that can annotate a React Query to facilitate invalidating with re-fetch for some time onwards */
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- interface didn't work here for reasons not fully understood. see https://stackoverflow.com/a/65799591
export type VyneMeta<T> = {
    vyneRefetcher: Refetcher<T>;
};

interface VyneQuery<T> {
    meta: VyneMeta<T>;
}

/** Invalidate queries (as with invalidateQueries), and keep re-fetching for specified duration
 * @param untilState If provided, will be called with the current state of the query to determine
 * if it should continue to refetch. A response of `true` stops refetching
 */
export async function invalidateQueriesFor<T>(
    queryClient: QueryClient,
    duration: number,
    queryKey: QueryKey,
    untilState?: (state: QueryState<T>) => boolean,
) {
    const cache = queryClient.getQueryCache();

    // configure refetch on existing queries
    for (const { meta } of cache.findAll({ queryKey })) {
        refetch(meta);
    }

    // listen for future queries and configure refetch on them
    const unsubscribe = cache.subscribe((notification) => {
        if (notification.type !== "added") return;
        const { query } = notification;
        for (const { meta } of cache.findAll({ queryKey, predicate: (x) => x === query })) {
            refetch(meta);
        }
    });

    setTimeout(unsubscribe, duration);

    // refetch existing queries now
    await queryClient.invalidateQueries({ queryKey });

    // refetcher for a particular query's metadata
    function refetch(meta: Record<string, unknown> | undefined) {
        if (isVyneMeta<T>(meta)) {
            meta.vyneRefetcher.refetch(duration, untilState);
        }
    }
}

class Refetcher<T> {
    #interval: number | false;
    #untilStates: Set<(state: QueryState<T>) => boolean>;

    constructor(interval: number | false) {
        this.#interval = interval;
        this.#untilStates = new Set();
    }

    getInterval(state: QueryState<T>) {
        return this.#should(state) ? this.#interval : false;
    }

    #should(state: QueryState<T>) {
        // A response of `true` from ALL #untilStates stops refetching
        return Array.from(this.#untilStates).some((x) => !x(state));
    }

    refetch(duration: number, untilState?: (state: QueryState<T>) => boolean) {
        untilState ??= () => false; // for purely duration-based refetches, use a function that never calls for refetches to stop

        setTimeout(() => this.#untilStates.delete(untilState), duration);
        this.#untilStates.add(untilState);
    }
}

function isVyneMeta<T>(meta: unknown): meta is VyneMeta<T> {
    return (
        typeof meta === "object" && meta !== null && "vyneRefetcher" in meta && meta.vyneRefetcher instanceof Refetcher
    );
}

/** Get the next refetch interval for a query that supports "invalidateQueriesFor" */
export function getRefetchInterval<T>(query: PossiblyVyneQuery<T>): number | false {
    if (!isVyneQuery(query)) {
        return false;
    }
    const { vyneRefetcher } = query.meta;
    return vyneRefetcher.getInterval(query.state);
}

function isVyneQuery<T>(query: unknown): query is VyneQuery<T> {
    return typeof query === "object" && query !== null && "meta" in query && isVyneMeta(query.meta);
}

/** Get initial metadata for a query that supports "invalidateQueriesFor" */
export const useRefetchMeta = <T>(vyneRefetchInterval: number | false): VyneMeta<T> => {
    const metaData = useMemo(
        () => ({
            vyneRefetcher: new Refetcher<T>(vyneRefetchInterval),
        }),
        [vyneRefetchInterval],
    );

    return metaData;
};
